// The sheets on the customer screens: add a customer (a rep for their own dealer,
// a Super admin for any dealer, Sales at Phoneware for the house dealer, each with
// the reasons Omni refuses), give an existing
// Autotask company to a dealer, and pick a customer's OneBill account.
import React, { useEffect, useState } from "react";
import {
  exactName,
  type AutotaskCategoryLink,
  type ContactInput,
  type CustomerContact,
  type CustomerInput,
  type CustomerSummary,
  type CustomerV2,
  type Dealer,
  type Order,
} from "@phoneware/lens-domain";
import { ApiError } from "../api.js";
import { categoryLinkOf, companyTypeName, type CategoryStatus, type DuplicateCompany, type OneBillAccountMatch } from "../api-customers.js";
import { Icon } from "../icons.js";
import { Link, navigate } from "../router.js";
import { useLoad, useSession } from "../session.js";
import { errorText, isEmail } from "../format.js";
import { Callout, Sheet, useToast } from "../ui.js";
import { whyText } from "./customer-bits.js";

interface Refusal {
  title: string;
  text: string;
}

/** Why Omni can't add a customer for this dealer yet, in the words the reader can act on. null when it can. */
function refusalFor(dealer: Dealer, link: AutotaskCategoryLink | null, category: CategoryStatus | null, ops: boolean): Refusal | null {
  if (!link) {
    return {
      title: "No Autotask category",
      text: ops
        ? `${dealer.name} has no Autotask category, and Omni files every new customer under its dealer's category. Link one in Dealers first.`
        : "Your dealer isn't linked to an Autotask category yet. Phoneware sets this up; until then Omni can't add customers for you.",
    };
  }
  if (category && !category.active) {
    return {
      title: "Category gone",
      text: ops
        ? `${dealer.name}'s Autotask category, ${category.name}, is inactive. Link an active category in Dealers first.`
        : "Your Autotask category is inactive. Phoneware has to fix it before Omni can add customers for you.",
    };
  }
  if (!dealer.autotaskOwner) {
    return {
      title: "No account manager in Autotask",
      text: ops
        ? `${dealer.name} has no account manager in Autotask. Set one in Dealers first.`
        : "Phoneware hasn't set your account manager in Autotask yet. Until then Omni can't add customers for you.",
    };
  }
  return null;
}

/** The company a 409 from POST /api/customers names, when the server sent one we can read. */
function duplicateOf(err: unknown): DuplicateCompany | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body;
  if (!body || typeof body !== "object" || !("duplicate" in body)) return null;
  const d = body.duplicate;
  if (!d || typeof d !== "object" || !("autotaskCompanyId" in d) || !("name" in d) || !("companyType" in d)) return null;
  const { autotaskCompanyId, name, companyType } = d;
  if (typeof autotaskCompanyId !== "number" || typeof name !== "string" || typeof companyType !== "number") return null;
  const categoryId = "categoryId" in d && typeof d.categoryId === "number" ? d.categoryId : null;
  const dealerId = "dealerId" in d && typeof d.dealerId === "string" ? d.dealerId : null;
  return { autotaskCompanyId, name, companyType, categoryId, dealerId };
}

const EMPTY_FORM = { name: "", phone: "", line1: "", line2: "", city: "", state: "", postalCode: "", first: "", last: "", email: "", contactPhone: "", title: "" };

/** Add a customer: Omni creates a Prospect in Autotask under the dealer's category and reads it back. */
export function AddCustomerSheet({ onClose }: { onClose: () => void }): React.ReactElement {
  const { me, api, can, everyDealer, canWrite } = useSession();
  const ops = everyDealer;
  const toast = useToast();
  const allDealers = useLoad<Dealer[]>(() => (ops ? api.dealers() : Promise.resolve([])), [ops]);
  // Only the dealers this person may add customers for: any for a Super admin, the house dealer for Sales at Phoneware.
  const dealers = { data: allDealers.data?.filter((d) => canWrite("customers.write", d)) ?? null };
  const categories = useLoad<CategoryStatus[]>(() => (ops ? api.autotaskCategories() : Promise.resolve([])), [ops]);
  const [dealerId, setDealerId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateCompany | null>(null);
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const dealer = ops ? dealers.data?.find((d) => d.id === dealerId) ?? null : me.dealer;
  const link = dealer ? categoryLinkOf(dealer) : null;
  const category = link ? categories.data?.find((c) => c.id === link.id) ?? null : null;
  const refusal = dealer ? refusalFor(dealer, link, category, ops) : null;
  const categoryName = (id: number | null) => (id === null ? "no category" : categories.data?.find((c) => c.id === id)?.name ?? `category ${id}`);
  const ready =
    dealer !== null &&
    refusal === null &&
    form.name.trim() && form.phone.trim() && form.line1.trim() && form.city.trim() && form.state.trim() && form.postalCode.trim() && form.first.trim() && form.last.trim() && isEmail(form.email);

  async function save(): Promise<void> {
    if (!dealer) return;
    const input: CustomerInput = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      address: { line1: form.line1.trim(), line2: form.line2.trim() || null, city: form.city.trim(), state: form.state.trim(), postalCode: form.postalCode.trim() },
      contact: { firstName: form.first.trim(), lastName: form.last.trim(), email: form.email.trim(), phone: form.contactPhone.trim() || null, title: form.title.trim() || null },
    };
    setBusy(true);
    setError(null);
    setDuplicate(null);
    try {
      const c = await api.createCustomer(ops ? { ...input, dealerId: dealer.id } : input);
      toast(`${c.name} is in Autotask as a Prospect, under ${dealer.name}.`);
      navigate(`/customers/${c.autotaskCompanyId}`);
    } catch (err) {
      const dup = duplicateOf(err);
      if (dup) setDuplicate(dup);
      else setError(errorText(err));
      setBusy(false);
    }
  }

  async function assignDuplicate(): Promise<void> {
    if (!duplicate || !dealer) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api.assignCustomer({ autotaskCompanyId: duplicate.autotaskCompanyId, dealerId: dealer.id, ownerEmail: null, expectedDealerId: duplicate.dealerId });
      toast(`${c.name} now belongs to ${dealer.name}.`);
      navigate(`/customers/${c.autotaskCompanyId}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const disabled = refusal !== null;
  return (
    <Sheet
      title="Add a customer"
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="customer-add-save" disabled={!ready || busy} onClick={() => void save()}>
            <Icon name="user-plus" size={15} />
            {busy ? "Adding" : "Add to Autotask"}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="customer-add-sheet">
        {ops ? (
          <label className="field">
            <span>Dealer</span>
            <select className="input" data-testid="customer-add-dealer" value={dealerId} onChange={(e) => { setDealerId(e.target.value); setDuplicate(null); setError(null); }}>
              <option value="">Pick the dealer it belongs to</option>
              {[...(dealers.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {refusal ? (
          <div className="callout stop" role="alert" data-testid="customer-add-refusal">
            <Icon name="ban" size={15} />
            <div>
              <p>
                <b>{refusal.title}.</b> {refusal.text}
              </p>
              {ops ? (
                <div className="btnrow">
                  <Link className="btn small white" to="/dealers">
                    Open Dealers
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        ) : dealer && link && dealer.autotaskOwner ? (
          <Callout icon="info">
            Omni creates it in Autotask as a <b>Prospect</b> in {link.name}, with {dealer.autotaskOwner.name} as account manager, then reads it back. It becomes a Customer when its first order goes through, and gets a OneBill account only then.
          </Callout>
        ) : null}
        {duplicate ? (
          <div className="callout stop" role="alert" data-testid="customer-add-duplicate">
            <Icon name="triangle-alert" size={15} />
            <div>
              <p>
                <b>Duplicate found.</b>{" "}
                {ops ? (
                  <>
                    Already in Autotask: <b>{duplicate.name}</b> {"\u00b7"} {companyTypeName(duplicate.companyType)} {"\u00b7"} {categoryName(duplicate.categoryId)} {"\u00b7"} company {duplicate.autotaskCompanyId}.{" "}
                    {duplicate.dealerId
                      ? `It's already in Omni under ${allDealers.data?.find((d) => d.id === duplicate.dealerId)?.name ?? "another dealer"}.`
                      : "Omni hasn't listed it because the import takes only Customers."}{" "}
                    Nothing was created.
                  </>
                ) : (
                  "A company with this name or phone is already in Autotask. Omni didn't create a second one. Ask Phoneware to assign it to you."
                )}
              </p>
              {ops ? (
                <div className="btnrow">
                  {duplicate.dealerId ? (
                    <Link className="btn small white" to={`/customers/${duplicate.autotaskCompanyId}`}>
                      Open it
                    </Link>
                  ) : dealer && !refusal && can("customers.assign") ? (
                    <button className="btn small white" type="button" data-testid="customer-add-assign" disabled={busy} onClick={() => void assignDuplicate()}>
                      Assign it to {dealer.name}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="formgrid">
          <label className="field full">
            <span>Company name</span>
            <input className="input" autoComplete="organization" disabled={disabled} value={form.name} onChange={set("name")} />
          </label>
          <label className="field">
            <span>Main phone</span>
            <input className="input" type="tel" autoComplete="tel" required disabled={disabled} value={form.phone} onChange={set("phone")} />
          </label>
          <label className="field full">
            <span>Street</span>
            <input className="input" autoComplete="address-line1" disabled={disabled} value={form.line1} onChange={set("line1")} />
          </label>
          <label className="field full">
            <span>Suite or unit</span>
            <input className="input" autoComplete="address-line2" disabled={disabled} value={form.line2} onChange={set("line2")} />
          </label>
          <label className="field">
            <span>City</span>
            <input className="input" autoComplete="address-level2" disabled={disabled} value={form.city} onChange={set("city")} />
          </label>
          <label className="field">
            <span>State</span>
            <input className="input" autoComplete="address-level1" disabled={disabled} value={form.state} onChange={set("state")} />
          </label>
          <label className="field">
            <span>ZIP</span>
            <input className="input" autoComplete="postal-code" disabled={disabled} value={form.postalCode} onChange={set("postalCode")} />
          </label>
          <label className="field">
            <span>Contact first name</span>
            <input className="input" autoComplete="given-name" disabled={disabled} value={form.first} onChange={set("first")} />
          </label>
          <label className="field">
            <span>Contact last name</span>
            <input className="input" autoComplete="family-name" disabled={disabled} value={form.last} onChange={set("last")} />
          </label>
          <label className="field full">
            <span>Contact email</span>
            <input className="input" type="email" autoComplete="email" disabled={disabled} value={form.email} onChange={set("email")} />
          </label>
          <label className="field">
            <span>Contact phone (optional)</span>
            <input className="input" type="tel" disabled={disabled} value={form.contactPhone} onChange={set("contactPhone")} />
          </label>
          <label className="field">
            <span>Contact title (optional)</span>
            <input className="input" disabled={disabled} value={form.title} onChange={set("title")} />
          </label>
        </div>
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

const EMPTY_CONTACT = { first: "", last: "", email: "", phone: "", title: "" };

interface ContactRefusal {
  title: string;
  sub: string | null;
  short: string;
}

/** Why Autotask didn't get the contact, in words the reader can act on. A 409 means the email is already on the company. */
function contactRefusal(err: unknown, customer: CustomerV2, email: string): ContactRefusal {
  if (err instanceof ApiError && err.status === 409) {
    const holder = customer.contacts.find((x) => (x.email ?? "").trim().toLowerCase() === email.toLowerCase());
    return {
      title: holder ? `${holder.name} already uses ${holder.email} on ${customer.name}.` : `${email} is already on ${customer.name} in Autotask.`,
      sub: "Each contact needs its own email. Nothing was added to Autotask.",
      short: "That email is already on this customer.",
    };
  }
  return { title: errorText(err), sub: "Nothing was added to Autotask.", short: "Autotask didn't add the contact." };
}

/** Add a contact: Omni adds the person to the customer's company in Autotask and reads it back. Anyone who can see the customer. */
export function AddContactSheet({ customer, onClose, onAdded }: { customer: CustomerV2; onClose: () => void; onAdded: (contact: CustomerContact) => void }): React.ReactElement {
  const { api } = useSession();
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<ContactRefusal | null>(null);
  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setRefused(null);
  };
  const named = Boolean(form.first.trim() && form.last.trim());
  const emailed = isEmail(form.email);
  const ready = named && emailed;
  const why = refused ? refused.short : !named ? "First and last name are needed." : !form.email.trim() ? "An email is needed too." : !emailed ? "Check the email." : "";

  async function save(): Promise<void> {
    if (!ready) return;
    const input: ContactInput = {
      firstName: form.first.trim(),
      lastName: form.last.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      title: form.title.trim() || null,
    };
    setBusy(true);
    setRefused(null);
    try {
      onAdded(await api.addContact(customer.autotaskCompanyId, input));
    } catch (err) {
      setRefused(contactRefusal(err, customer, input.email));
      setBusy(false);
    }
  }

  const req = (
    <span className="req" aria-hidden="true">
      {" *"}
    </span>
  );
  return (
    <Sheet
      title="Add a contact"
      label={`Add a contact to ${customer.name}`}
      meta={customer.name}
      onClose={onClose}
      foot={
        <>
          <span className={refused ? "why full stop" : "why full"} data-testid="contact-why">
            {why}
          </span>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="contact-save" disabled={!ready || busy} onClick={() => void save()}>
            <Icon name="user-plus" size={15} />
            {busy ? "Adding" : "Add contact"}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="contact-sheet">
        <p className="ns-note">Omni adds the contact to {customer.name} in Autotask and reads it back.</p>
        {refused ? (
          <div className="callout stop" role="alert" data-testid="contact-refusal">
            <Icon name="triangle-alert" size={15} />
            <div>
              <b>{refused.title}</b>
              {refused.sub ? <span className="sub-l">{refused.sub}</span> : null}
            </div>
          </div>
        ) : null}
        <div className="formgrid">
          <label className="field">
            <span>First name{req}</span>
            <input className="input" data-testid="contact-first" autoComplete="off" required value={form.first} onChange={set("first")} />
          </label>
          <label className="field">
            <span>Last name{req}</span>
            <input className="input" data-testid="contact-last" autoComplete="off" required value={form.last} onChange={set("last")} />
          </label>
          <label className="field full">
            <span>Email{req}</span>
            <input className="input" data-testid="contact-email" type="email" autoComplete="off" required value={form.email} onChange={set("email")} />
          </label>
          <label className="field">
            <span>Phone</span>
            <input className="input" data-testid="contact-phone" type="tel" autoComplete="off" value={form.phone} onChange={set("phone")} />
          </label>
          <label className="field">
            <span>Title</span>
            <input className="input" data-testid="contact-title" autoComplete="off" value={form.title} onChange={set("title")} />
          </label>
        </div>
        <div>
          <h3 className="sheet-h3">Already on {customer.name}</h3>
          {customer.contacts.length === 0 ? (
            <p className="ns-note">No contacts in Autotask yet.</p>
          ) : (
            <ul className="contacts people" data-testid="contact-existing">
              {customer.contacts.map((x) => (
                <li key={x.autotaskContactId}>
                  <div>
                    <b>{x.name}</b>
                    <span className="row-m">{x.email ?? "No email"}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Sheet>
  );
}

/** Gives a customer to another dealer. Omni writes that dealer's category to Autotask; billing and history stay. Ops only. */
export function AssignSheet({ customer, onClose, onAssigned }: { customer: CustomerSummary; onClose: () => void; onAssigned: (c: CustomerSummary) => void }): React.ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const dealers = useLoad<Dealer[]>(() => api.dealers(), []);
  const [dealerId, setDealerId] = useState("");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = dealerId !== "" && dealerId !== customer.dealerId && (owner.trim() === "" || isEmail(owner));

  async function assign(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const c = await api.assignCustomer({ autotaskCompanyId: customer.autotaskCompanyId, dealerId, ownerEmail: owner.trim() || null, expectedDealerId: customer.dealerId });
      toast(`${c.name} now belongs to ${dealers.data?.find((d) => d.id === dealerId)?.name ?? "the dealer"}.`);
      onAssigned(c);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Sheet
      title="Assign to a dealer"
      onClose={onClose}
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="customer-assign-save" disabled={!ready || busy} onClick={() => void assign()}>
            {busy ? "Assigning" : "Assign"}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="card-p">Move {customer.name} to another dealer. Omni writes that dealer's Autotask category and reads it back; its OneBill account and history stay.</p>
        <label className="field">
          <span>Dealer</span>
          <select className="input" data-testid="customer-assign-dealer" value={dealerId} onChange={(e) => setDealerId(e.target.value)}>
            <option value="">Pick a dealer</option>
            {[...(dealers.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)).map((d) => (
              <option key={d.id} value={d.id} disabled={d.id === customer.dealerId}>
                {d.name}
                {d.id === customer.dealerId ? " (now)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Owner at the dealer (optional)</span>
          <input className="input" type="email" placeholder="Their Omni email" value={owner} onChange={(e) => setOwner(e.target.value)} />
        </label>
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}

export interface BillingSheetProps {
  customer: CustomerSummary;
  /** Every customer ops can see, to say when an account already bills someone else. */
  everyone: CustomerSummary[];
  /** An account to start with, from the OneBill review; linking it settles that review item. */
  preselect: { accountNumber: string; name: string } | null;
  /** The order this customer's billing is holding, if any. */
  held: Order | null;
  onDone: () => void;
  onClose: () => void;
}

/** Pick the OneBill account that bills this customer, or confirm it has none. Ops only. */
export function BillingSheet({ customer, everyone, preselect, held, onDone, onClose }: BillingSheetProps): React.ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const billing = customer.billing;
  const candidates = billing.state === "unreviewed" ? billing.candidates : [];
  const current = billing.state === "linked" ? billing.accountNumber : null;
  const [sel, setSel] = useState<string>(preselect?.accountNumber ?? current ?? "");
  const [find, setFind] = useState("");
  const [found, setFound] = useState<OneBillAccountMatch[] | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [ackName, setAckName] = useState(false);
  const [ackShared, setAckShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const query = find.trim();

  useEffect(() => {
    if (query.length < 2) {
      setFound(null);
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      api.onebillAccounts(query).then(
        (list) => {
          if (!live) return;
          setFound(list);
          setSearchError(null);
        },
        (err: unknown) => {
          if (live) setSearchError(errorText(err));
        },
      );
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [api, query]);

  // The chosen account's name, from wherever it was offered.
  const known: Record<string, string> = {};
  for (const c of candidates) known[c.accountNumber] = c.name;
  for (const a of found ?? []) known[a.accountNumber] = a.name;
  if (preselect) known[preselect.accountNumber] = preselect.name;
  const others = (account: string) => everyone.filter((x) => x.autotaskCompanyId !== customer.autotaskCompanyId && x.billing.state === "linked" && x.billing.accountNumber === account);
  const chosenName = sel && sel !== "none" ? known[sel] ?? null : null;
  const needName = chosenName !== null && exactName(chosenName) !== exactName(customer.name);
  const sharing = sel && sel !== "none" ? others(sel) : [];
  const needShared = sharing.length > 0;
  const ok = sel === "none" || (sel !== "" && sel !== current && (!needName || ackName) && (!needShared || ackShared));

  function choose(account: string): void {
    setSel(account);
    setAckName(false);
    setAckShared(false);
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (sel === "none") await api.setCustomerBilling(customer.autotaskCompanyId, { action: "confirm_none" });
      else if (preselect && preselect.accountNumber === sel) await api.decideBillingReview(sel, { action: "link", autotaskCompanyId: customer.autotaskCompanyId });
      else await api.setCustomerBilling(customer.autotaskCompanyId, { action: "link", accountNumber: sel });
      const retry = held ? ` Retry ${held.number} to bill it.` : "";
      toast(sel === "none" ? `Saved: ${customer.name} has no OneBill account.${retry}` : `Linked ${sel} to ${customer.name}.${retry}`);
      onDone();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  const option = (account: string, name: string, meta: React.ReactNode, tag: string | null) => {
    const also = others(account);
    return (
      <li key={account}>
        <label className="pick">
          <input type="radio" name="billing-pick" value={account} checked={sel === account} onChange={() => choose(account)} data-testid={`billing-pick-${account}`} />
          <span>
            {tag ? <span className="chip why">{tag}</span> : null}
            <b>{name}</b>
            <span className="row-m mono">{meta}</span>
            {also.length ? <span className="row-m">Already bills {also.map((x) => x.name).join(", ")}</span> : null}
          </span>
        </label>
      </li>
    );
  };

  // What the sheet offers first: the account linked now, the one the review pointed at, then the sync's suggestions.
  const suggested: Array<{ account: string; name: string; tag: string }> = [];
  if (current) suggested.push({ account: current, name: known[current] ?? current, tag: "Linked" });
  if (preselect && preselect.accountNumber !== current) suggested.push({ account: preselect.accountNumber, name: preselect.name, tag: "From review" });
  for (const c of candidates) if (!suggested.some((s) => s.account === c.accountNumber)) suggested.push({ account: c.accountNumber, name: c.name, tag: whyText(c) });
  return (
    <Sheet
      title={`OneBill account for ${customer.name}`}
      onClose={onClose}
      wide
      foot={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" type="button" data-testid="billing-save" disabled={!ok || busy} onClick={() => void save()}>
            {busy ? "Saving" : sel === "none" ? "Confirm no account" : "Link account"}
          </button>
        </>
      }
    >
      <div className="stack" data-testid="billing-sheet">
        <p className="card-p">
          Pick the OneBill account that bills <b>{customer.name}</b>, or confirm it has none.
        </p>
        <h3>{current ? "Linked now" : "Suggested"}</h3>
        {suggested.length ? (
          <ul className="picks">{suggested.map((s) => option(s.account, s.name, s.account, s.tag))}</ul>
        ) : (
          <p className="field-note">No account scores 50% on name, and none shares its phone.</p>
        )}
        <h3>Search OneBill</h3>
        <label className="search">
          <Icon name="search" size={15} />
          <span className="sr">Search OneBill accounts</span>
          <input className="input" type="search" placeholder="Account name or number" data-testid="billing-search" value={find} onChange={(e) => setFind(e.target.value)} />
        </label>
        {searchError ? (
          <Callout kind="stop" icon="triangle-alert">
            {searchError}
          </Callout>
        ) : query.length < 2 ? (
          <p className="field-note">Type two letters of an account name or number.</p>
        ) : found === null ? (
          <p className="field-note">Searching OneBill.</p>
        ) : found.length === 0 ? (
          <p className="field-note" data-testid="billing-search-none">No OneBill account matches "{query}".</p>
        ) : (
          <ul className="picks">{found.map((a) => option(a.accountNumber, a.name, `${a.accountNumber} \u00b7 ${a.status}`, null))}</ul>
        )}
        <h3>No account</h3>
        <ul className="picks">
          <li>
            <label className="pick">
              <input type="radio" name="billing-pick" value="none" checked={sel === "none"} onChange={() => choose("none")} data-testid="billing-pick-none" />
              <span>
                <b>{customer.name} has no OneBill account</b>
                <span className="row-m">Its first order creates one from the Autotask address and billing contact.</span>
              </span>
            </label>
          </li>
        </ul>
        {needName && chosenName ? (
          <>
            <div className="callout warn" data-testid="billing-name-differs">
              <Icon name="triangle-alert" size={15} />
              <p>
                The names differ. OneBill has <b>{chosenName}</b>; Autotask has <b>{customer.name}</b>.
              </p>
            </div>
            <label className="chk">
              <input type="checkbox" data-testid="billing-ack-name" checked={ackName} onChange={(e) => setAckName(e.target.checked)} />
              <span>I checked that this account bills {customer.name}.</span>
            </label>
          </>
        ) : null}
        {needShared ? (
          <>
            <div className="callout warn" data-testid="billing-shared">
              <Icon name="users" size={15} />
              <p>
                {sel} already bills <b>{sharing.map((x) => x.name).join(", ")}</b>
                {sharing.some((x) => x.autotaskCompanyId === customer.parentCompanyId) ? ", its parent company" : ""}.
              </p>
            </div>
            <label className="chk">
              <input type="checkbox" data-testid="billing-ack-shared" checked={ackShared} onChange={(e) => setAckShared(e.target.checked)} />
              <span>Both companies bill on this one account.</span>
            </label>
          </>
        ) : null}
        {error ? (
          <Callout kind="stop" icon="triangle-alert">
            {error}
          </Callout>
        ) : null}
      </div>
    </Sheet>
  );
}
